import { CreateDocumentData, DocumentFileLanguage } from '@adminStore/documents';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiConfiguration } from '@klubovna/api-client';
import {
  documentsAdminControllerCreate,
  DocumentsAdminControllerCreate$Params,
  documentsAdminControllerDelete,
  documentsAdminControllerDeleteFile,
  DocumentsAdminControllerDeleteFile$Params,
  documentsAdminControllerFindAll,
  DocumentsAdminControllerFindAll$Params,
  documentsAdminControllerFindOne,
  documentsAdminControllerUpdate,
  DocumentsAdminControllerUpdate$Params,
  documentsAdminControllerUploadFile,
  DocumentsAdminControllerUploadFile$Params,
} from '@klubovna/api-client';
import { Document, UpdateDocumentDto } from '@klubovna/api-client';
import { PaginatedResponse, toPaginatedResponse } from '@klubovna/api-client';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface DocumentsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
  sortBy?: 'identifier' | 'name' | 'nameEn' | 'createdAt' | 'isActive';
  sortOrder?: 'ASC' | 'DESC';
}

@Injectable({
  providedIn: 'root',
})
export class DocumentsApiService {
  private http = inject(HttpClient);
  private apiConfig = inject(ApiConfiguration);

  findAll(params?: DocumentsQueryParams): Observable<PaginatedResponse<Document>> {
    const apiParams: DocumentsAdminControllerFindAll$Params = {
      page: params?.page,
      limit: params?.limit,
      search: params?.search,
      isActive: params?.isActive,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
    };

    return documentsAdminControllerFindAll(this.http, this.apiConfig.rootUrl, apiParams)
      .pipe(map(response => toPaginatedResponse<Document>(response.body)));
  }

  findOne(id: string): Observable<Document> {
    return documentsAdminControllerFindOne(this.http, this.apiConfig.rootUrl, { id })
      .pipe(map(response => response.body as Document));
  }

  create(documentData: CreateDocumentData): Observable<Document> {
    const params: DocumentsAdminControllerCreate$Params = {
      body: {
        identifier: documentData.identifier,
        name: documentData.name,
        nameEn: documentData.nameEn,
        path: documentData.path,
        pathEn: documentData.pathEn,
        isActive: documentData.isActive,
        file: documentData.file,
        fileEn: documentData.fileEn,
      },
    };

    return documentsAdminControllerCreate(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => response.body as Document));
  }

  update(id: string, documentData: UpdateDocumentDto): Observable<Document> {
    const params: DocumentsAdminControllerUpdate$Params = {
      id,
      body: documentData,
    };
    return documentsAdminControllerUpdate(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => response.body as Document));
  }

  delete(id: string): Observable<void> {
    return documentsAdminControllerDelete(this.http, this.apiConfig.rootUrl, { id })
      .pipe(map(() => undefined));
  }

  uploadFile(id: string, file: File, lang?: DocumentFileLanguage): Observable<Document> {
    const params: DocumentsAdminControllerUploadFile$Params = {
      id,
      lang,
      body: { file },
    };
    return documentsAdminControllerUploadFile(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => response.body as Document));
  }

  deleteFile(id: string, lang?: DocumentFileLanguage): Observable<Document> {
    const params: DocumentsAdminControllerDeleteFile$Params = {
      id,
      lang,
    };
    return documentsAdminControllerDeleteFile(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => response.body as Document));
  }
}
